import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';

import { paths } from '@common/types';
import { stableKey } from '@common/cache/util.ts';

interface IProviderListProps {
  search: string;
  page: number;
  limit: number;
  sort_order?: string;
  sort_by?: string;
  state?: string;
  partner_id?: string;
  practice_id?: string;
  guide_certified?: string;
  status?: string;
  provider_specialty:
    | ''
    | 'Cardiology'
    | 'Geropsych'
    | 'Palliative care'
    | 'Primary care'
    | 'Neurology'
    | 'Social Worker';
  region_ids?: string[];
  clinical_leader_ids?: string[];
  supervising_physician_ids?: string[];
}

const providerListRequest = async (props: IProviderListProps) => {
  const response = axiosInstance.get<
    paths[`/record/providers`]['get']['responses']['200']['content']['application/json']
  >(`/record/providers`, {
    params: {
      page: props.page,
      limit: props.limit,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.search !== '' && { search: props.search }),
      ...(props.state !== '' && { state: upperCase(props.state) }),
      ...(props.partner_id !== '' && { partner_id: props.partner_id }),
      ...(props.practice_id !== '' && { practice_id: props.practice_id }),
      ...(props.status !== '' && { inactive: props.status === 'inactive' }),
      ...(props.provider_specialty !== '' && {
        provider_specialty: props.provider_specialty,
      }),
      ...(props.guide_certified !== '' && {
        guide_certified: props.guide_certified,
      }),
      ...(props.region_ids?.length && { region_ids: props.region_ids }),
      ...(props.clinical_leader_ids?.length && {
        clinical_leader_ids: props.clinical_leader_ids,
      }),
      ...(props.supervising_physician_ids?.length && {
        supervising_physician_ids: props.supervising_physician_ids,
      }),
    },
  });
  const data = (await response).data;

  return data;
};

export const useProviderListQuery = (props: IProviderListProps) => {
  const query = useQuery({
    queryKey: ['providers', stableKey(props)],
    queryFn: () => providerListRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

interface IProviderProps {
  provider_id: string;
}

const providerRequest = async (props: IProviderProps) => {
  const response = axiosInstance.get<
    paths[`/provider/{record_id}`]['get']['responses']['200']['content']['application/json']
  >(`/provider/${props.provider_id}`);
  const data = (await response).data;

  return data;
};

export const useProviderQuery = (props: IProviderProps) => {
  const query = useQuery({
    queryKey: ['provider', props.provider_id],
    queryFn: () => providerRequest(props),
  });

  return query;
};

interface IProviderHistoryProps {
  page: number;
  limit: number;
  provider_id: string;
  enabled: boolean;
}

const providerHistoryRequest = async (props: IProviderHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/provider/{record_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/provider/${props.provider_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const useProviderHistoryQuery = ({
  provider_id,
  ...props
}: IProviderHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['provider', provider_id, 'history', stableKey(props)],
    queryFn: () => providerHistoryRequest({ provider_id, ...props }),
  });

  return query;
};

interface IProvidersShortQueryProps {
  search?: string;
  record_id?: string;
}

const providersShortRequest = async ({
  search,
  record_id,
}: IProvidersShortQueryProps) => {
  const response = axiosInstance.get<
    paths[`/record/providers/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/providers/short`, {
    params: {
      ...(search && { search }),
      record_id: record_id || undefined,
    },
  });
  const data = (await response).data;
  return data;
};

export const useProvidersShortQuery = (
  props: IProvidersShortQueryProps & { disabled?: boolean },
) => {
  const orgQuery = useQuery({
    enabled: !props.disabled,
    queryKey: ['providersShort', props.search, props.record_id],
    queryFn: () => providersShortRequest(props),
  });

  return orgQuery;
};

const providerRegionShortRequest = async (search?: string) => {
  const response = axiosInstance.get<
    paths[`/provider-region/short`]['get']['responses']['200']['content']['application/json']
  >(`/provider-region/short`, {
    params: {
      ...(search && { search }),
    },
  });
  const data = (await response).data;
  return data;
};

export const useProviderRegionShortQuery = (search?: string) => {
  const orgQuery = useQuery({
    queryKey: ['providerRegion', search],
    // enabled: !!(search && search.length > 2),
    queryFn: () => providerRegionShortRequest(search),
  });

  return orgQuery;
};

const providerClinicalLeaderShortRequest = async (search?: string) => {
  const response = axiosInstance.get<
    paths[`/clinical-leader/short`]['get']['responses']['200']['content']['application/json']
  >(`/clinical-leader/short`, {
    params: {
      ...(search && { search }),
    },
  });
  const data = (await response).data;
  return data;
};

export const useProviderClinicalShortQuery = (search?: string) => {
  const orgQuery = useQuery({
    queryKey: ['providerClinicalLeader', search],
    // enabled: !!(search && search.length > 2),
    queryFn: () => providerClinicalLeaderShortRequest(search),
  });

  return orgQuery;
};
